import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { BookOpen, Clock, ShieldCheck, CheckCircle2, Calendar, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

// Parse admin-stored datetime-local string ("2026-05-15T10:30") as IST.
function parseExamDateIST(s) {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  // `new Date("2026-05-15T10:30")` is interpreted in the browser's LOCAL tz.
  // For the most predictable experience (our users are in India), we treat the
  // raw admin string as IST regardless of the student's browser timezone.
  const istOffsetMs = 5.5 * 60 * 60 * 1000;
  const localOffsetMs = -d.getTimezoneOffset() * 60 * 1000;
  return new Date(d.getTime() + (istOffsetMs - localOffsetMs));
}

function formatIST(d) {
  if (!d) return "";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  }).format(d);
}

function ScheduleCountdown({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return null;
  const diff = target.getTime() - now;
  if (diff <= 0) return <span className="text-emerald-600 font-bold">Exam is live now</span>;
  const sec = Math.floor(diff / 1000);
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  const text = d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`;
  return <span className="font-mono text-brand font-bold">{text}</span>;
}

export default function StudentTestSeries() {
  const { user, formatApiError } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(null);
  const [regTest, setRegTest] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", college: "" });
  const [referralCode, setReferralCode] = useState("");
  const [referralInfo, setReferralInfo] = useState(null);   // {valid, active, name, college}
  const [referralChecking, setReferralChecking] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get("/student/test-series");
    setItems(r.data || []);
  };

  const openRegistration = (test) => {
    setForm({
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      college: user?.college || "",
    });
    // Pre-fill referral code from URL ?ref=
    try {
      const url = new URL(window.location.href);
      const ref = url.searchParams.get("ref") || "";
      setReferralCode(ref.toUpperCase());
    } catch { setReferralCode(""); }
    setReferralInfo(null);
    setRegTest(test);
  };

  const checkReferral = async () => {
    const c = (referralCode || "").trim().toUpperCase();
    if (!c) { setReferralInfo({ valid: false }); return; }
    setReferralChecking(true);
    try {
      const { data } = await api.get(`/public/verify-ambassador-code?code=${encodeURIComponent(c)}`);
      setReferralInfo(data);
      if (!data.valid) toast.error("Invalid referral code");
      else if (!data.active) toast.warning("Code valid but ambassador is inactive — discount applies, no commission");
      else toast.success(`10% off applied (${data.name})`);
    } catch { toast.error("Check failed"); }
    finally { setReferralChecking(false); }
  };

  const proceed = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error("Name, email and phone are required");
      return;
    }
    // Save profile details so admin can see them
    try {
      await api.put("/me/profile", { name: form.name, phone: form.phone, college: form.college });
    } catch {}
    const test = regTest;
    setLoading(test.id);
    setRegTest(null);
    try {
      const ref = (referralInfo && referralInfo.valid) ? `&referral_code=${encodeURIComponent(referralCode.toUpperCase())}` : "";
      const { data } = await api.post(`/payments/initiate?test_id=${test.id}&return_origin=${encodeURIComponent(window.location.origin)}${ref}`);
      if (data.free) {
        toast.success("Registered successfully!");
        load();
      } else if (data.payu) {
        toast.info("Redirecting to PayU...");
        const formEl = document.createElement("form");
        formEl.method = "POST";
        formEl.action = data.payu.action;
        for (const [k, v] of Object.entries(data.payu)) {
          if (k === "action") continue;
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = k;
          input.value = v;
          formEl.appendChild(input);
        }
        document.body.appendChild(formEl);
        formEl.submit();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(null);
    }
  };

  return (
    <DashboardLayout>
      <h1 className="heading text-2xl font-bold">Test Series</h1>
      <p className="text-sm text-slate-500 mt-1">Register → {`{Pay if paid}`} → Take the secure proctored exam. Result is announced by Admin.</p>
      {items.length === 0 ? (
        <div className="mt-8 p-14 rounded-2xl border border-dashed border-slate-300 text-center">
          <BookOpen size={40} className="mx-auto text-slate-300" />
          <div className="mt-3 text-slate-500">No tests available yet.</div>
        </div>
      ) : (
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((t) => {
            const examAt = parseExamDateIST(t.exam_date);
            const isLocked = examAt && examAt.getTime() > Date.now();
            return (
            <div key={t.id} className="p-6 rounded-2xl border border-slate-200 bg-white" data-testid={`test-card-${t.id}`}>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded ${t.is_free ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {t.is_free ? "Free" : `₹${t.price}`}
                </span>
                <span className="text-xs text-slate-500 flex items-center gap-1"><Clock size={12} /> {t.duration_minutes} min</span>
              </div>
              <h3 className="heading text-lg font-bold mt-3">{t.title}</h3>
              <p className="mt-2 text-sm text-slate-600 line-clamp-2">{t.description}</p>

              {examAt && (
                <div className={`mt-3 p-3 rounded-lg border text-xs ${isLocked ? "bg-blue-50 border-blue-200" : "bg-emerald-50 border-emerald-200"}`} data-testid={`test-schedule-${t.id}`}>
                  <div className="flex items-center gap-1.5 text-slate-600 font-bold uppercase tracking-[0.15em] text-[10px]">
                    <Calendar size={12} /> Exam schedule (IST)
                  </div>
                  <div className="mt-1 font-semibold text-slate-800">{formatIST(examAt)}</div>
                  {isLocked && (
                    <div className="mt-1 text-slate-600">
                      Starts in <ScheduleCountdown target={examAt} />
                    </div>
                  )}
                </div>
              )}
              {t.result_date && (
                <div className="mt-2 text-[11px] text-slate-500">
                  Result date: <b className="text-slate-700">{t.result_date}</b>
                </div>
              )}

              <div className="mt-4">
                {t.is_attempted ? (
                  <span className="text-sm text-slate-500 font-semibold inline-flex items-center gap-1"><CheckCircle2 size={14} /> Already attempted</span>
                ) : t.is_enrolled ? (
                  isLocked ? (
                    <button disabled className="bg-slate-200 text-slate-500 font-semibold rounded-full px-5 py-2 text-sm inline-flex items-center gap-2 cursor-not-allowed" data-testid={`exam-locked-${t.id}`}>
                      <Lock size={14} /> Locked until exam time
                    </button>
                  ) : (
                    <Link to={`/exam/${t.id}`} className="bg-brand hover:bg-[#172b66] text-white font-semibold rounded-full px-5 py-2 text-sm inline-block" data-testid={`start-exam-${t.id}-button`}>
                      Start Exam →
                    </Link>
                  )
                ) : (
                  <button
                    onClick={() => openRegistration(t)}
                    disabled={loading === t.id}
                    className="bg-brand hover:bg-[#172b66] disabled:opacity-60 text-white font-semibold rounded-full px-5 py-2 text-sm"
                    data-testid={`register-test-${t.id}-button`}
                  >
                    {loading === t.id ? "Processing..." : t.is_free ? "Register (Free)" : `Register & Pay ₹${t.price}`}
                  </button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Registration Modal */}
      <Dialog open={!!regTest} onOpenChange={(o) => !o && setRegTest(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="test-reg-modal">
          <DialogHeader>
            <DialogTitle>Test Registration — {regTest?.title}</DialogTitle>
            <DialogDescription>
              Verify your details before {regTest?.is_free ? "registering" : "proceeding to payment"}.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-500">Please verify your details before {regTest?.is_free ? "registering" : "proceeding to payment"}. An entrance pass will be linked to this information.</p>
          <div className="grid grid-cols-1 gap-3 mt-3">
            {[
              ["Full Name *", "name"],
              ["Email *", "email", true],
              ["Phone *", "phone"],
              ["College / Institution", "college"],
            ].map(([label, key, disabled]) => (
              <div key={key}>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
                <input
                  value={form[key]}
                  disabled={disabled}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-brand outline-none disabled:bg-slate-50 disabled:text-slate-500"
                  data-testid={`test-reg-${key}-input`}
                />
              </div>
            ))}
          </div>
          {!regTest?.is_free && (
            <div className="mt-3 p-3 rounded-lg border border-amber-200 bg-amber-50/50">
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-amber-800">College Ambassador Referral Code (Optional)</label>
              <div className="mt-1 flex gap-2">
                <input
                  value={referralCode}
                  onChange={(e) => { setReferralCode(e.target.value.toUpperCase()); setReferralInfo(null); }}
                  placeholder="AMBXXXXX  (10% off)"
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm font-mono"
                  data-testid="referral-code-input"
                />
                <button
                  type="button"
                  onClick={checkReferral}
                  disabled={referralChecking || !referralCode}
                  className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-bold rounded-lg px-4 py-2 text-sm"
                  data-testid="referral-apply-btn"
                >
                  {referralChecking ? "..." : "Apply"}
                </button>
              </div>
              {referralInfo?.valid && (
                <div className="mt-2 text-xs text-emerald-700 font-semibold flex items-center gap-1" data-testid="referral-applied-msg">
                  ✓ 10% discount applied — referred by {referralInfo.name}
                  {!referralInfo.active && <span className="text-amber-600">(ambassador inactive — no commission, but discount holds)</span>}
                </div>
              )}
              {referralInfo && !referralInfo.valid && (
                <div className="mt-2 text-xs text-red-600 font-semibold">Invalid referral code</div>
              )}
            </div>
          )}
          <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-brand flex gap-2">
            <ShieldCheck size={16} className="shrink-0 mt-0.5" />
            <div>
              {regTest?.is_free
                ? "This test is FREE. You'll be registered and can start the exam immediately."
                : referralInfo?.valid
                  ? `You'll be redirected to PayU secure gateway to pay ₹${(regTest?.price * 0.9).toFixed(0)} (10% off applied).`
                  : `You'll be redirected to PayU secure gateway to pay ₹${regTest?.price}. After successful payment, you'll be enrolled.`}
            </div>
          </div>
          <DialogFooter className="sticky bottom-0 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 px-4 sm:px-6 py-3 bg-white border-t border-slate-100 flex-row justify-end gap-2">
            <button onClick={() => setRegTest(null)} className="px-4 py-2 text-sm font-semibold" data-testid="test-reg-cancel">Cancel</button>
            <button
              onClick={proceed}
              disabled={loading === regTest?.id}
              className="bg-brand hover:bg-[#172b66] text-white font-bold rounded-lg px-5 py-2 text-sm disabled:opacity-60"
              data-testid="test-reg-submit"
            >
              {regTest?.is_free ? "Complete Registration" :
                referralInfo?.valid ? `Proceed to Pay ₹${(regTest?.price * 0.9).toFixed(0)}`
                : `Proceed to Pay ₹${regTest?.price}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
